import React from 'react';
import { withRouter } from 'react-router-dom';
import { withApollo } from 'react-apollo';
import compose from 'lodash/flowRight';
// Local Imports
import OrganizationsView from '../../../platform/ux/Orgs/OrganizationsView';
// Utility Imports
import { permissionsFromRoles, hasRealFeatures } from '../../utils/dataManipulating/organization';
import { getDefaultChildLevel } from '../../utils/orgPermissions';
import { resolveModelType } from '../../utils/pricing/orgPricing';
import { configType, permission, defaultCPM } from '../../utils/constants/constants';
// Queries
import GET_ORG_DETAIL from '../../GraphQl/Queries/GET_ORG_DETAIL';

// constants
const { DATACONFIG, CONFIG } = configType;
const { READ, WRITE } = permission;
class Controller extends React.Component {
  state = {
    currentOrg: null,
    currentRole: null,
    selectedOrg: null,
    loadingOrgDetail: false,
  };
  componentDidMount = async () => {
    const {
      match: {
        params: { org },
      },
      currentuser: { role, features },
    } = this.props;
    // NOTE: to have re-usable roles this setup will need to be changed to pull from a single Org attached to the User
    const currentRole = role;
    currentRole.features = features;

    // Fetch current org full detail for currentOrg state
    const currentOrgTree = this.props.organizations.find((o) => o.id === currentRole.org.id);
    let currentOrg;
    try {
      const { data } = await this.props.client.query({
        query: GET_ORG_DETAIL,
        variables: { where: { id: currentRole.org.id } },
        fetchPolicy: 'network-only',
      });
      currentOrg = {
        ...data.org,
        features: permissionsFromRoles([data.org.roles.find((role) => role.isMaster)]),
        modelType: resolveModelType(data.org.config, data.org.pricingModel),
      };
    } catch (err) {
      // Fallback to tree data if detail fetch fails
      currentOrg = currentOrgTree
        ? {
            ...currentOrgTree,
            features: permissionsFromRoles([currentOrgTree.roles.find((role) => role.isMaster)]),
            modelType: resolveModelType(currentOrgTree.config, currentOrgTree.pricingModel),
          }
        : null;
    }

    this.setState({
      currentOrg,
      currentRole,
    });

    // Fetch selected org detail
    const targetOrgId = org || currentRole.org.id;
    this.fetchAndSelectOrg(targetOrgId);
  };

  fetchAndSelectOrg = async (orgId) => {
    this.setState({ loadingOrgDetail: true });
    try {
      const { data } = await this.props.client.query({
        query: GET_ORG_DETAIL,
        variables: { where: { id: orgId } },
        fetchPolicy: 'network-only',
      });
      let org = data.org;
      if (org) {
        // Fetch parent org features if this org has a parent
        let parentFeatures = null;
        let parentConfig = null;

        if (org.parentOrg && org.parentOrg.length > 0) {
          const directParentId = org.parentOrg[org.parentOrg.length - 1].id;
          try {
            const { data: parentData } = await this.props.client.query({
              query: GET_ORG_DETAIL,
              variables: { where: { id: directParentId } },
              fetchPolicy: 'cache-first', // Use cache if available
            });
            if (parentData?.org) {
              parentFeatures = permissionsFromRoles([
                parentData.org.roles.find((role) => role.isMaster),
              ]);
              parentConfig = parentData.org.config;
            }
          } catch (parentErr) {
            console.error('Error fetching parent org detail:', parentErr);
          }
        }

        this.processAndSetSelectedOrg(org, parentFeatures, parentConfig);
      }
    } catch (err) {
      console.error('Error fetching org detail:', err);
      // Fallback: try using tree data
      const treeOrg = this.props.organizations.find((o) => o.id === orgId);
      if (treeOrg) {
        this.processAndSetSelectedOrg(treeOrg);
      }
    } finally {
      this.setState({ loadingOrgDetail: false });
    }
  };

  processAndSetSelectedOrg = (org, parentFeatures = null, parentConfig = null) => {
    // set a default data for dataConfig
    if (org.dataConfig == null) {
      org.dataConfig = {
        reTargeting: false,
        appIds: [],
        tradeDeskIdentifier: null,
        storageBucket: null,
        googleCustomerId: null,
        defaultDisplayAttributionWindow: null,
        defaultPaidSearchAttributionWindow: null,
        defaultSEOAttributionWindow: null,
        seo: false,
        semrushEnable: false,
        semrushConfig: {
          projectId: null,
          rootDomainUrl: null,
          rootKeyword: null,
          competitor1stRootDomainUrl: null,
          competitor2ndRootDomainUrl: null,
        },
        googleAnalytics: {
          viewId: null,
          isGA4: null,
        },
        paidSearchDataConfig: {
          isEnabled: null,
        },
        tagProcessingConfig: {
          processTag: null,
        },
      };
    }

    if (org.dataConfig.googleAnalytics == null) {
      org.dataConfig.googleAnalytics = { viewId: null };
    }

    if (org.dataConfig.semrushConfig == null) {
      org.dataConfig.semrushConfig = {
        projectId: null,
        rootDomainUrl: null,
        rootKeyword: null,
        competitor1stRootDomainUrl: null,
        competitor2ndRootDomainUrl: null,
      };
    }

    if (org.pricingModel == null) {
      org = {
        modelType: 'CPM',
        contract: false,
        displayCPM: '7',
        displayContractCPM: '7',
        videoCPM: '7',
        videoContractCPM: '7',
        premiumDisplayCPM: '12',
        premiumDisplayContractCPM: '12',
        tvOttStandardCPM: '35',
        tvOttStandardContractCPM: '35',
        tvOttPremiumCPM: '40',
        tvOttPremiumContractCPM: '40',
        percentage: '20',
        contractPercentage: '20',
        ...org,
      };
    } else {
      const { pricingModel } = org;
      const cpmList = pricingModel.cpmList || [];
      const displayCPM = cpmList.find((cpm) => cpm.type === 'DISPLAY');
      const videoCPM = cpmList.find((cpm) => cpm.type === 'VIDEO');
      const premiumDisplayCPM = cpmList.find((cpm) => cpm.type === 'PREMIUM_DISPLAY');
      const tvOttStandardCPM = cpmList.find((cpm) => cpm.type === 'TV_OTT_STANDARD');
      const tvOttPremiumCPM = cpmList.find((cpm) => cpm.type === 'TV_OTT_PREMIUM');
      org = {
        modelType: resolveModelType(org.config, pricingModel),
        contract: pricingModel.hasContract,
        displayCPM: displayCPM?.advertiserCPM || '7',
        displayContractCPM: displayCPM?.contractCPM || '7',
        videoCPM: videoCPM?.advertiserCPM || '7',
        videoContractCPM: videoCPM?.contractCPM || '7',
        premiumDisplayCPM: premiumDisplayCPM?.advertiserCPM || '12',
        premiumDisplayContractCPM: premiumDisplayCPM?.contractCPM || '12',
        tvOttStandardCPM: tvOttStandardCPM?.advertiserCPM || '35',
        tvOttStandardContractCPM: tvOttStandardCPM?.contractCPM || '35',
        tvOttPremiumCPM: tvOttPremiumCPM?.advertiserCPM || '40',
        tvOttPremiumContractCPM: tvOttPremiumCPM?.contractCPM || '40',
        percentage: pricingModel.auctionPlus?.advertiserPercentage || '20',
        contractPercentage: pricingModel.auctionPlus?.contractPercentage || '20',
        ...org,
      };
    }

    if (org.dataConfig.paidSearchDataConfig == null) {
      org.dataConfig.paidSearchDataConfig = { isEnabled: false };
    }

    if (org.dataConfig.tagProcessingConfig == null) {
      org.dataConfig.tagProcessingConfig = { isEnabled: false };
    }

    const selectedOrgWithFeatures = {
      ...org,
      features: permissionsFromRoles([org.roles.find((role) => role.isMaster)]),
      parentFeatures, // Add parent features for child org editing
      parentConfig, // Add parent config for child org editing
    };
    this.setState({
      selectedOrg: selectedOrgWithFeatures,
    });
  };

  onSelectingOrg = (currentRole, org) => {
    const { organizations } = this.props;

    let selectedOrg = org
      ? organizations.find((organization) => organization.id === org)
      : organizations.find((organization) => organization.id === currentRole.org.id);

    if (selectedOrg) {
      // set a default data for dataConfig
      if (selectedOrg.dataConfig == null) {
        selectedOrg.dataConfig = {
          reTargeting: false,
          appIds: [],
          tradeDeskIdentifier: null,
          storageBucket: null,
          googleCustomerId: null,
          defaultDisplayAttributionWindow: null,
          defaultPaidSearchAttributionWindow: null,
          defaultSEOAttributionWindow: null,
          seo: false,
          semrushEnable: false,
          semrushConfig: {
            projectId: null,
            rootDomainUrl: null,
            rootKeyword: null,
            competitor1stRootDomainUrl: null,
            competitor2ndRootDomainUrl: null,
          },
          googleAnalytics: {
            viewId: null,
            isGA4: null,
          },
          paidSearchDataConfig: {
            isEnabled: null,
          },
          tagProcessingConfig: {
            processTag: null,
          },
        };
      }

      if (selectedOrg.dataConfig.googleAnalytics == null) {
        selectedOrg.dataConfig.googleAnalytics = { viewId: null };
      }

      if (selectedOrg.dataConfig.semrushConfig == null) {
        selectedOrg.dataConfig.semrushConfig = {
          projectId: null,
          rootDomainUrl: null,
          rootKeyword: null,
          competitor1stRootDomainUrl: null,
          competitor2ndRootDomainUrl: null,
        };
      }
      if (selectedOrg.pricingModel == null) {
        selectedOrg = {
          modelType: 'CPM',
          contract: false,
          displayCPM: '7',
          displayContractCPM: '7',
          videoCPM: '7',
          videoContractCPM: '7',
          percentage: '20',
          contractPercentage: '20',
          ...selectedOrg,
        };
      } else {
        const { pricingModel } = selectedOrg;
        const cpmList = pricingModel.cpmList || [];
        const displayCPM = cpmList.find((cpm) => cpm.type === 'DISPLAY');
        const videoCPM = cpmList.find((cpm) => cpm.type === 'VIDEO');
        const premiumDisplayCPM = cpmList.find((cpm) => cpm.type === 'PREMIUM_DISPLAY');
        const tvOttStandardCPM = cpmList.find((cpm) => cpm.type === 'TV_OTT_STANDARD');
        const tvOttPremiumCPM = cpmList.find((cpm) => cpm.type === 'TV_OTT_PREMIUM');
        selectedOrg = {
          modelType: resolveModelType(selectedOrg.config, pricingModel),
          contract: pricingModel.hasContract,
          displayCPM: displayCPM?.advertiserCPM || '7',
          displayContractCPM: displayCPM?.contractCPM || '7',
          videoCPM: videoCPM?.advertiserCPM || '7',
          videoContractCPM: videoCPM?.contractCPM || '7',
          premiumDisplayCPM: premiumDisplayCPM?.advertiserCPM || '12',
          premiumDisplayContractCPM: premiumDisplayCPM?.contractCPM || '12',
          tvOttStandardCPM: tvOttStandardCPM?.advertiserCPM || '35',
          tvOttStandardContractCPM: tvOttStandardCPM?.contractCPM || '35',
          tvOttPremiumCPM: tvOttPremiumCPM?.advertiserCPM || '40',
          tvOttPremiumContractCPM: tvOttPremiumCPM?.contractCPM || '40',
          percentage: pricingModel.auctionPlus?.advertiserPercentage || '20',
          contractPercentage: pricingModel.auctionPlus?.contractPercentage || '20',
          ...selectedOrg,
        };
      }

      if (selectedOrg.dataConfig.paidSearchDataConfig == null) {
        selectedOrg.dataConfig.paidSearchDataConfig = { isEnabled: false };
      }

      if (selectedOrg.dataConfig.tagProcessingConfig == null) {
        selectedOrg.dataConfig.tagProcessingConfig = { isEnabled: false };
      }
    }

    const selectedOrgWithFeatures = selectedOrg
      ? {
          ...selectedOrg,
          features: permissionsFromRoles([selectedOrg.roles.find((role) => role.isMaster)]),
        }
      : null;
    this.setState({
      selectedOrg: selectedOrgWithFeatures,
    });
  };

  onChange = (key, value, type) => {
    let updatedOrg = { ...this.state.selectedOrg };
    if (type === 'semrushConfig') {
      if (!updatedOrg.dataConfig) {
        updatedOrg.dataConfig = {
          semrushConfig: {
            projectId: null,
            rootDomainUrl: null,
            rootKeyword: null,
            competitor1stRootDomainUrl: null,
            competitor2ndRootDomainUrl: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      } else if (!updatedOrg.dataConfig.semrushConfig) {
        let data = updatedOrg.dataConfig;
        updatedOrg.dataConfig = {
          ...data,
          semrushConfig: {
            projectId: null,
            rootDomainUrl: null,
            rootKeyword: null,
            competitor1stRootDomainUrl: null,
            competitor2ndRootDomainUrl: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      }

      updatedOrg.dataConfig.semrushConfig[key] = value.trim();

      this.setState({
        selectedOrg: updatedOrg,
      });
    }

    if (type === 'googleAnalytics') {
      if (!updatedOrg.dataConfig) {
        updatedOrg.dataConfig = {
          googleAnalytics: {
            viewId: null,
            isGA4: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      } else if (!updatedOrg.dataConfig.googleAnalytics) {
        let data = updatedOrg.dataConfig;
        updatedOrg.dataConfig = {
          ...data,
          googleAnalytics: {
            viewId: null,
            isGA4: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      }
      updatedOrg.dataConfig.googleAnalytics[key] = key[0] === 'viewId' ? value.trim() : value;

      this.setState({
        selectedOrg: updatedOrg,
      });
    }

    if (type === 'bing') {
      if (!updatedOrg.dataConfig) {
        updatedOrg.dataConfig = {
          bing: {
            accountId: null,
            customerId: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      } else if (!updatedOrg.dataConfig.bing) {
        let data = updatedOrg.dataConfig;
        updatedOrg.dataConfig = {
          ...data,
          bing: {
            accountId: null,
            customerId: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      }

      updatedOrg.dataConfig.bing[key] = value.trim();

      this.setState({
        selectedOrg: updatedOrg,
      });
    }
    if (type === 'paidSearchDataConfig') {
      if (!updatedOrg.dataConfig) {
        updatedOrg.dataConfig = {
          paidSearchDataConfig: {
            isEnabled: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      } else if (!updatedOrg.dataConfig.paidSearchDataConfig) {
        let data = updatedOrg.dataConfig;
        updatedOrg.dataConfig = {
          ...data,
          paidSearchDataConfig: {
            isEnabled: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });

        updatedOrg.dataConfig.paidSearchDataConfig[key] = value;
        this.setState({
          selectedOrg: updatedOrg,
        });
      }
    }

    if (type === 'tagProcessingConfig') {
      if (!updatedOrg.dataConfig) {
        updatedOrg.dataConfig = {
          tagProcessingConfig: {
            processTag: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      } else if (!updatedOrg.dataConfig.tagProcessingConfig) {
        let data = updatedOrg.dataConfig;
        updatedOrg.dataConfig = {
          ...data,
          tagProcessingConfig: {
            processTag: null,
          },
        };
        this.setState({
          selectedOrg: updatedOrg,
        });

        updatedOrg.dataConfig.tagProcessingConfig[key] = value;
        this.setState({
          selectedOrg: updatedOrg,
        });
      }
    }

    if (type === DATACONFIG) {
      if (!updatedOrg.dataConfig) {
        updatedOrg.dataConfig = {};
        this.setState({
          selectedOrg: updatedOrg,
        });
      }

      if (key[0] === 'appIds') {
        updatedOrg.dataConfig[key] = value;
      } else {
        updatedOrg.dataConfig[key] = value.trim();
      }

      this.setState({
        selectedOrg: updatedOrg,
      });
    } else {
      updatedOrg[key] = value;
      this.setState({
        selectedOrg: updatedOrg,
      });
    }
  };

  onSwitchChange = (checked, data, field) => {
    const [feature, permission] = data.split('-');
    const updatedOrg = { ...this.state.selectedOrg };

    if (permission === CONFIG) {
      if (!updatedOrg.config) {
        updatedOrg.config = {};
        this.setState({
          selectedOrg: updatedOrg,
        });
      }

      updatedOrg.config[feature] = checked;
    } else if (permission === DATACONFIG) {
      if (!updatedOrg.dataConfig) {
        updatedOrg.dataConfig = {};
        this.setState({
          selectedOrg: updatedOrg,
        });
      }

      if (!updatedOrg.dataConfig.tagProcessingConfig) {
        updatedOrg.dataConfig.tagProcessingConfig = {
          processTag: false,
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      }

      if (!updatedOrg.dataConfig.paidSearchDataConfig) {
        updatedOrg.dataConfig.paidSearchDataConfig = {
          isEnabled: false,
        };
        this.setState({
          selectedOrg: updatedOrg,
        });
      }
      if (field) {
        updatedOrg.dataConfig[feature][field] = checked;
      } else {
        updatedOrg.dataConfig[feature] = checked;
      }
    } else {
      if (checked) {
        updatedOrg.features[feature] = [READ, WRITE];
      } else {
        delete updatedOrg.features[feature];
      }
    }

    this.setState({
      selectedOrg: updatedOrg,
    });
  };

  createOrg = async () => {
    const { createOrg } = this.props;

    const {
      name,
      description,
      level,
      chatWootId,
      cpm,
      rpm,
      website,
      domain,
      logo,
      features,
      parentOrg,
      config,
      dataConfig,
      signInLogo,
      platformDomain,
      tagDomain,
      CPAcronym,
      zone,
      modelType,
      contract,
      displayCPM,
      displayContractCPM,
      videoCPM,
      videoContractCPM,
      premiumDisplayCPM,
      premiumDisplayContractCPM,
      tvOttStandardCPM,
      tvOttStandardContractCPM,
      tvOttPremiumCPM,
      tvOttPremiumContractCPM,
      percentage,
      contractPercentage,
      salesforceId,
      billingEmail,
    } = this.state.selectedOrg;

    const roleitems = Object.entries(features).map(([feature, permission]) => {
      const record = {
        feature: feature,
        actions: { set: permission },
      };

      return record;
    });

    if (roleitems.length === 0) {
      let error = 'feature in empty';
      throw error;
    }
    // Adds ENABLED feature with READ and WRITE
    roleitems.push({
      feature: 'ENABLED',
      actions: { set: ['READ', 'WRITE'] },
    });

    const noUrlWebsite = website.replace(/^https?:\/\//i, '');

    const data = {
      name,
      description,
      level,
      chatWootId,
      cpm: cpm.toLocaleString(),
      rpm,
      website: noUrlWebsite,
      domain,
      logo: logo.id,
      features,
      parentOrgId: parentOrg[parentOrg.length - 1],
      parentOrg,
      roleitems,
      config,
      dataConfig,
      signInLogo,
      platformDomain,
      tagDomain,
      CPAcronym,
      zone,
      modelType,
      contract,
      displayCPM: displayCPM.toLocaleString(),
      displayContractCPM: displayContractCPM.toLocaleString(),
      videoCPM: videoCPM.toLocaleString(),
      videoContractCPM: videoContractCPM.toLocaleString(),
      premiumDisplayCPM: (premiumDisplayCPM ?? '12').toLocaleString(),
      premiumDisplayContractCPM: (premiumDisplayContractCPM ?? '12').toLocaleString(),
      tvOttStandardCPM: (tvOttStandardCPM ?? '35').toLocaleString(),
      tvOttStandardContractCPM: (tvOttStandardContractCPM ?? '35').toLocaleString(),
      tvOttPremiumCPM: (tvOttPremiumCPM ?? '40').toLocaleString(),
      tvOttPremiumContractCPM: (tvOttPremiumContractCPM ?? '40').toLocaleString(),
      percentage: percentage.toLocaleString(),
      contractPercentage: contractPercentage.toLocaleString(),
      salesforceId,
      billingEmail,
    };
    const response = await createOrg(data);
    const { status, error } = response;

    if (status === 'success') {
      this.setState({
        selectedOrg: {
          ...response.data,
          features: permissionsFromRoles(response.data.roles),
        },
      });
      return;
    } else if (status === 'failure') {
      throw error;
    }
  };

  updateOrg = async () => {
    const { editOrg, organizations } = this.props;

    const {
      selectedOrg: {
        name,
        description,
        level,
        chatWootId,
        cpm,
        rpm,
        website,
        domain,
        logo,
        id,
        features,
        roles,
        config,
        dataConfig,
        signInLogo,
        platformDomain,
        tagDomain,
        CPAcronym,
        zone,
        modelType,
        contract,
        displayCPM,
        displayContractCPM,
        videoCPM,
        videoContractCPM,
        premiumDisplayCPM,
        premiumDisplayContractCPM,
        tvOttStandardCPM,
        tvOttStandardContractCPM,
        tvOttPremiumCPM,
        tvOttPremiumContractCPM,
        percentage,
        contractPercentage,
        pricingModel,
        salesforceId,
        billingEmail,
      },
      currentOrg,
    } = this.state;

    const roleitems = Object.entries(features).map(([feature, permission]) => {
      const record = {
        feature: feature,
        actions: { set: permission },
      };

      return record;
    });

    // Block saving an org with no real features. Checking real (non-ENABLED)
    // features directly also covers disabled orgs, where ENABLED is absent so a
    // plain length check would miss the zero-feature case (#10643).
    if (!hasRealFeatures(features)) {
      let error = 'feature in empty';
      throw error;
    }

    const noUrlWebsite = website.replace(/^https?:\/\//i, '');

    if (!dataConfig.bing) {
      dataConfig.bing = {
        accountId: null,
        customerId: null,
      };
    }

    // Get the correct parent org ID from the selectedOrg's parentOrg array
    // If parentOrg array exists and has items, use the last one (direct parent)
    // Otherwise, fall back to currentOrg.id for backward compatibility
    const parentOrgId =
      this.state.selectedOrg.parentOrg && this.state.selectedOrg.parentOrg.length > 0
        ? this.state.selectedOrg.parentOrg[this.state.selectedOrg.parentOrg.length - 1].id
        : currentOrg.id;

    const data = {
      name,
      description,
      level,
      chatWootId,
      cpm: cpm ? cpm.toLocaleString() : defaultCPM.cpm,
      rpm,
      noUrlWebsite,
      domain,
      logo,
      id,
      features,
      parentOrgId,
      roleitems,
      roles,
      defaultOrg: currentOrg.name,
      config,
      dataConfig,
      signInLogo,
      website,
      platformDomain,
      tagDomain,
      CPAcronym,
      zone,
      modelType,
      contract,
      displayCPM,
      displayContractCPM,
      videoCPM,
      videoContractCPM,
      premiumDisplayCPM,
      premiumDisplayContractCPM,
      tvOttStandardCPM,
      tvOttStandardContractCPM,
      tvOttPremiumCPM,
      tvOttPremiumContractCPM,
      percentage,
      contractPercentage,
      pricingModel,
      salesforceId,
      billingEmail,
    };
    const response = await editOrg(data);

    const { status } = response;
    if (status === 'success') {
      const updatedOrg = response.data;
      if (updatedOrg.id === this.state.selectedOrg.id) {
        // Once updated performs an update on the isAdmin role's id if still on the same Org's details
        const indexToUpdate = this.state.selectedOrg.roles.findIndex((role) => role.isMaster);
        const updatedRoles = this.state.selectedOrg.roles.slice();
        updatedRoles[indexToUpdate].id = updatedOrg.roles[0].id;
        const indexToUpdateOrg = organizations.findIndex((org) => org.id === updatedOrg.id);
        organizations.splice(indexToUpdateOrg, 1, updatedOrg);
        this.setState({
          selectedOrg: {
            ...this.state.selectedOrg,
            roles: updatedRoles,
          },
        });
        return true;
      }
    } else if (status === 'failure') {
      const gqlError = response['error'];
      if (
        gqlError?.message?.includes('unique constraint') &&
        gqlError?.message?.includes('Field name = name')
      ) {
        throw new Error(errorType.nameDuplicate);
      }

      // Re-throw with the full GraphQL error so the UI can parse the field name
      if (gqlError?.message) {
        throw new Error(gqlError.message.replace('GraphQL error: ', ''));
      }

      throw status;
    }
  };
  //here

  deleteOrg = async () => {
    const { deleteOrg, history } = this.props;
    const { selectedOrg } = this.state;
    const response = await deleteOrg(selectedOrg);
    const { status, error } = response;
    if (status === 'success') {
      this.onSelectingOrg(this.state.currentRole, this.state.currentRole.org.id);
      history.push(`/orgs/${this.state.currentRole.org.id}`);
    } else if (status === 'failure') {
      // Surface the backend's actual message — it may name blocking entities
      // (e.g. "has 3 LIVE/LIVE_APPROVED campaigns ..." or "has 2 non-archived
      // child orgs ...") so the operator knows exactly what to fix.
      const backendMessage =
        (error && error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) ||
        (error && error.message) ||
        'An error has occurred while writing to the db, the organization has not been deleted';
      throw new Error(backendMessage);
    }
  };

  fullDeleteOrg = async () => {
    const { fullDeleteOrg, history } = this.props;
    const { selectedOrg } = this.state;
    const response = await fullDeleteOrg(selectedOrg);
    const { status, error } = response;
    if (status === 'success') {
      this.onSelectingOrg(this.state.currentRole, this.state.currentRole.org.id);
      history.push(`/orgs/${this.state.currentRole.org.id}`);
    } else if (status === 'failure') {
      const backendMessage =
        (error && error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) ||
        (error && error.message) ||
        'An error has occurred while writing to the db, the organization has not been fully deleted';
      throw new Error(backendMessage);
    }
  };

  disableOrg = async () => {
    const { disableOrg } = this.props;
    const { selectedOrg } = this.state;
    const response = await disableOrg(selectedOrg);
    const { status } = response;

    if (status === 'success') {
      let disabledOrg = response.data;
      // set a default data for dataConfig
      if (disabledOrg.dataConfig == null) {
        disabledOrg.dataConfig = {
          reTargeting: false,
          appIds: [],
          tradeDeskIdentifier: null,
          storageBucket: null,
          googleCustomerId: null,
          seo: false,
          semrushEnable: false,
          semrushConfig: {
            projectId: null,
            rootDomainUrl: null,
            rootKeyword: null,
            competitor1stRootDomainUrl: null,
            competitor2ndRootDomainUrl: null,
          },
          googleAnalytics: {
            viewId: null,
          },
          paidSearchDataConfig: {
            isEnabled: null,
          },
          tagProcessingConfig: {
            processTag: null,
          },
        };
      }

      this.setState({
        selectedOrg: {
          // Enable/disable only toggles the org's ENABLED role, but the mutation
          // response is a partial org (no pricingModel/CPM fields; dataConfig
          // omits bing/tagProcessingConfig/paidSearchDataConfig). Replacing
          // selectedOrg with it drops those already-normalized fields, so a
          // follow-up Update throws on e.g. `displayCPM.toLocaleString()` before
          // the mutation can fire. Keep the normalized selectedOrg and only
          // refresh roles/features. This also preserves parentFeatures/
          // parentConfig, so the feature toggler keeps its full list (#10643).
          ...selectedOrg,
          roles: disabledOrg.roles,
          features: permissionsFromRoles(disabledOrg.roles),
        },
      });
    } else if (status === 'failure') {
      throw new Error(
        'An error has occurred while writing to the db, the organization has not been disabled'
      );
    }
  };

  enableOrg = async () => {
    const { enableOrg } = this.props;
    const { selectedOrg } = this.state;

    // An org must keep at least one real feature to be enabled. Without this
    // guard an org can be enabled with every feature turned off (#10643).
    if (!hasRealFeatures(selectedOrg.features)) {
      let error = 'feature in empty';
      throw error;
    }

    const response = await enableOrg(selectedOrg);
    const { status } = response;

    if (status === 'success') {
      let enabledOrg = response.data;
      // set a default data for dataConfig
      if (enabledOrg.dataConfig == null) {
        enabledOrg.dataConfig = {
          reTargeting: false,
          appIds: [],
          tradeDeskIdentifier: null,
          storageBucket: null,
          googleCustomerId: null,
          seo: false,
          semrushEnable: false,
          semrushConfig: {
            projectId: null,
            rootDomainUrl: null,
            rootKeyword: null,
            competitor1stRootDomainUrl: null,
            competitor2ndRootDomainUrl: null,
          },
          googleAnalytics: {
            viewId: null,
          },
          paidSearchDataConfig: {
            isEnabled: null,
          },
          tagProcessingConfig: {
            processTag: null,
          },
        };
      }

      this.setState({
        selectedOrg: {
          // Enable/disable only toggles the org's ENABLED role, but the mutation
          // response is a partial org (no pricingModel/CPM fields; dataConfig
          // omits bing/tagProcessingConfig/paidSearchDataConfig). Replacing
          // selectedOrg with it drops those already-normalized fields, so a
          // follow-up Update throws on e.g. `displayCPM.toLocaleString()` before
          // the mutation can fire. Keep the normalized selectedOrg and only
          // refresh roles/features. This also preserves parentFeatures/
          // parentConfig, so the feature toggler keeps its full list (#10643).
          ...selectedOrg,
          roles: enabledOrg.roles,
          features: permissionsFromRoles(enabledOrg.roles),
        },
      });
    } else if (status === 'failure') {
      throw new Error(
        'An error has occurred while writing to the db, the organization has not been enabled'
      );
    }
  };

  moveOrg = async (data) => {
    try {
      const response = await this.props.moveOrg({
        variables: data.variables,
      });

      if (response) {
        return {
          status: 'success',
          error: null,
          data: response.data.moveOrg,
        };
      }
      return response;
    } catch (err) {
      console.log('ERROR', err);
      return {
        status: 'failure',
        error: err,
      };
    }
  };

  clickCreateNewOrg = () => {
    const { selectedOrg } = this.state;
    const parentOrg = selectedOrg.parentOrg.slice();
    parentOrg.push({
      name: selectedOrg.name,
      id: selectedOrg.id,
    });

    // Default level should come from the org the user currently has selected,
    // because that is the parent the new org will be created under.
    const defaultLevel = getDefaultChildLevel(selectedOrg?.level);

    // When the selected parent org is disabled its master-role permissions are
    // stripped, so `selectedOrg.features` is empty and the child's Permissions /
    // Modules section renders nothing. Fall back only within the parent's own
    // lineage (its parent), then to empty — never to the logged-in admin's org,
    // which would grant features the parent hierarchy isn't entitled to confer.
    const hasKeys = (obj) => !!obj && Object.keys(obj).length > 0;
    const inheritedParentFeatures = hasKeys(selectedOrg.features)
      ? selectedOrg.features
      : hasKeys(selectedOrg.parentFeatures)
        ? selectedOrg.parentFeatures
        : {};
    const inheritedParentConfig = hasKeys(selectedOrg.config)
      ? selectedOrg.config
      : hasKeys(selectedOrg.parentConfig)
        ? selectedOrg.parentConfig
        : {};

    this.setState({
      selectedOrg: {
        name: '',
        description: '',
        level: defaultLevel,
        chatWootId: process.env.REACT_APP_CHATWOOT_ID
          ? process.env.REACT_APP_CHATWOOT_ID
          : process.env.REACT_APP_DEFAULT_CHATWOOT_ID,
        cpm: '7',
        rpm: 0,
        logo: {},
        website: '',
        domain: '',
        features: [],
        roles: {},
        parentOrg,
        config: {},
        zone: 'UTC',
        dataConfig: {
          reTargeting: false,
          appIds: [],
          tradeDeskIdentifier: null,
          storageBucket: null,
          googleCustomerId: null,
          seo: false,
          semrushEnable: false,
          semrushConfig: {
            projectId: null,
            rootDomainUrl: null,
            rootKeyword: null,
            competitor1stRootDomainUrl: null,
            competitor2ndRootDomainUrl: null,
          },
          googleAnalytics: {
            viewId: null,
          },
          paidSearchDataConfig: {
            isEnabled: null,
          },
          tagProcessingConfig: {
            processTag: null,
          },
        },
        modelType: selectedOrg.modelType || 'CPM',
        contract: selectedOrg.contract || false,
        displayCPM: selectedOrg.displayCPM || '7',
        displayContractCPM: selectedOrg.displayContractCPM || '7',
        videoCPM: selectedOrg.videoCPM || '7',
        videoContractCPM: selectedOrg.videoContractCPM || '7',
        premiumDisplayCPM: selectedOrg.premiumDisplayCPM || '12',
        premiumDisplayContractCPM: selectedOrg.premiumDisplayContractCPM || '12',
        tvOttStandardCPM: selectedOrg.tvOttStandardCPM || '35',
        tvOttStandardContractCPM: selectedOrg.tvOttStandardContractCPM || '35',
        tvOttPremiumCPM: selectedOrg.tvOttPremiumCPM || '40',
        tvOttPremiumContractCPM: selectedOrg.tvOttPremiumContractCPM || '40',
        percentage: selectedOrg.percentage || '20',
        contractPercentage: selectedOrg.contractPercentage || '20',
        // Store parent org's features and config for child org creation
        // This ensures the child can only be granted features/config the parent has.
        // Falls back through the parent chain when the immediate parent is disabled
        // (disabled orgs have empty features), so modules still render.
        parentFeatures: inheritedParentFeatures,
        parentConfig: inheritedParentConfig,
      },
    });
  };

  onOrgSelect = (org) => {
    const { match, history } = this.props;

    // Fetch full org details on demand
    this.fetchAndSelectOrg(org.id);
    history.push(`${match.path.replace(/:org\?\/:user\?/gi, org.id)}`);
  };

  render() {
    return (
      <OrganizationsView
        deleteOrg={this.deleteOrg}
        fullDeleteOrg={this.fullDeleteOrg}
        disableOrg={this.disableOrg}
        updateOrg={this.updateOrg}
        createOrg={this.createOrg}
        enableOrg={this.enableOrg}
        moveOrg={this.moveOrg}
        currentuser={this.props.currentuser}
        onChange={this.onChange}
        onSwitchChange={this.onSwitchChange}
        clickCreateNewOrg={this.clickCreateNewOrg}
        organizations={this.props.organizations}
        onOrgSelect={this.onOrgSelect}
        selectedOrg={this.state.selectedOrg}
        loadingOrgDetail={this.state.loadingOrgDetail}
        currentRole={this.state.currentRole}
        currentOrg={this.state.currentOrg}
        match={this.props.match}
        assumeRole={this.props.assumeRole}
        updateOrgSendEmailConfig={this.props.updateOrgSendEmailConfig}
        refetchOrganizations={this.props.refetchOrganizations}
        loadingOrgs={this.props.loadingOrgs}
        createDspAdvertiser={this.props.createDspAdvertiser}
        createSalesforceForOrg={this.props.createSalesforceForOrg}
      />
    );
  }
}

export default compose(withApollo, withRouter)(Controller);
